import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { comandasApi } from './api';
import type { AbrirComandaInput, AtualizarComandaInput, ComandaStatus, ConfigComandas } from './types';

export function useComandas(params: { status?: ComandaStatus; q?: string } = {}) {
  return useQuery({
    queryKey: ['comandas', params],
    queryFn: () => comandasApi.list(params),
    staleTime: 10_000,
  });
}

export function useComanda(id: string | null | undefined) {
  return useQuery({
    queryKey: ['comandas', id],
    queryFn: () => comandasApi.getById(id as string),
    enabled: !!id,
  });
}

export function useComandaResumo(id: string | null | undefined) {
  return useQuery({
    queryKey: ['comandas', id, 'resumo'],
    queryFn: () => comandasApi.resumo(id as string),
    enabled: !!id,
  });
}

export function useAbrirComanda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AbrirComandaInput) => comandasApi.abrir(body),
    onSuccess: () => {
      toast.success('Comanda aberta');
      qc.invalidateQueries({ queryKey: ['comandas'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useAtualizarComanda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AtualizarComandaInput }) =>
      comandasApi.atualizar(id, body),
    onSuccess: () => {
      toast.success('Comanda atualizada');
      qc.invalidateQueries({ queryKey: ['comandas'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useCancelarComanda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => comandasApi.cancelar(id),
    onSuccess: () => {
      toast.success('Comanda cancelada');
      qc.invalidateQueries({ queryKey: ['comandas'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useConfigComandas() {
  return useQuery({
    queryKey: ['configuracoes', 'comandas'],
    queryFn: () => comandasApi.getConfig(),
  });
}

export function useSetConfigComandas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigComandas) => comandasApi.setConfig(body),
    onSuccess: () => {
      toast.success('Configuração atualizada');
      qc.invalidateQueries({ queryKey: ['configuracoes'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useComandaItens(id: string | null | undefined) {
  return useQuery({
    queryKey: ['comandas', id, 'itens'],
    queryFn: () => comandasApi.getItens(id as string),
    enabled: !!id,
  });
}

export function useAdicionarItensComanda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { itens: any[] } }) =>
      comandasApi.adicionarItens(id, body),
    onSuccess: () => {
      toast.success('Itens adicionados à comanda');
      qc.invalidateQueries({ queryKey: ['comandas'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
